<script lang="ts">
	import type { InputSelectProps, InputTextProps } from '@wiseed/wikit';
	import type { AppInputCheckboxProps } from '@app/types/Inputs';
	import { InputCheckbox } from '@wiseed/wikit';
	import { InputText } from '@wiseed/wikit';
	import ResultList from '../ResultList.svelte';
	import { debounceAsync } from '@app/utils/global';
	import { getCompanyInfoBySIRET } from '@app/services/company';
	import type { ComponentType } from '@wiseed/wikit';
	import type { Company } from '@app/types/Company';
	import { t } from 'svelte-i18n';
	import CompanySuggestion from '../CompanySuggestion.svelte';
	import { removeWhiteSpace } from '@wiseed/wikit';
	import InputSelect from '@app/components/inputs/AppInputSelect.svelte';
	import { getContext, onMount } from 'svelte';
	import { page } from '$app/stores';
	import InputsAddresses from './InputsAddresses.svelte';
	import type { Writable } from 'svelte/store';

	export let nameProps: InputTextProps;
	export let sirenProps: InputTextProps;
	export let siretProps: InputTextProps;
	export let cityOfRegistration: InputTextProps;
	export let postalCode: InputTextProps | null = null;
	export let businessLine: InputSelectProps;
	export let enterInfoManuallyDescription: string | null = null;
	export let updateMode = false;
	export let addressCompany: InputTextProps;
	export let companyCityProps: InputTextProps;
	export let companyPostalCodeProps: InputTextProps;
	export let companyCountryProps: InputTextProps;
	export let companyNotFoundProps: AppInputCheckboxProps = {
		label: $t('companyNotFound'),
		description: enterInfoManuallyDescription ?? ''
	};
	$: companyNotFoundProps.disabled = siretProps.disabled;

	let isFetching = false;
	let forceShowCompanyInputs: Writable<boolean> = getContext('forceShowCompanyInputs');
	let isForeignCompany: Writable<boolean> = getContext('isForeignCompany');

	let INSEEProps: InputTextProps = {
		value: `${siretProps?.value ?? ''} ${
			nameProps?.value && addressCompany?.value
				? `(${nameProps?.value} - ${addressCompany?.value})`
				: ''
		}`
	};

	$: INSEEProps.label = $isForeignCompany
		? $t('kyc.inputs.registrationNumberSIREN')
		: $t('kyc.inputs.registrationNumberSIRET');

	$: if (siretProps) {
		siretProps.label = $isForeignCompany ? $t('kyc.inputs.registrationNumberSIREN') : 'SIRET';
		siretProps.hint = $isForeignCompany ? $t('kyc.inputs.registrationNumberSIRENHint') : undefined;
	}

	$: if (!sirenProps)
		sirenProps = {
			label: $t('kyc.inputs.registrationNumberSIRET'),
			value: $page?.data?.user?.legalPerson.registrationNumberSIRET,
			required: true
		};

	$: sirenProps.label = !$isForeignCompany ? 'SIREN' : $t('kyc.inputs.registrationNumberSIREN');

	let results: ComponentType[] | null = null;
	onMount(() => {
		forceShowInputsIfEmpty();
	});

	const forceShowInputsIfEmpty = () => {
		if ($$props['INSEEProps']?.required) {
			for (const prop in $$props) {
				if ($$props[prop]?.required && !$$props[prop]?.value && updateMode)
					companyNotFoundProps.checked = true;
			}
		}
	};

	$: {
		if ($forceShowCompanyInputs) {
			companyNotFoundProps.checked = true;
		} else if ($isForeignCompany) {
			companyNotFoundProps.checked = true;
			sirenProps.value = '000 000 000 00000';
			siretProps.error = '';
			companyNotFoundProps.disabled = true;
		} else {
			companyNotFoundProps.checked = false;
		}
	}

	$: if (companyNotFoundProps.checked) {
		INSEEProps.required = false;
		INSEEProps.value = '';
		INSEEProps.disabled = true;
	} else {
		INSEEProps.required = true;
		INSEEProps.disabled = siretProps.disabled;
	}

	let placesInputProps: InputTextProps = {};

	let onKeyUp = debounceAsync(async () => {
		const createResultComponent = (c: Company) => {
			return {
				component: CompanySuggestion,
				props: {
					label: `${c.name} - ${c.address}`,
					onClick: () => {
						if (nameProps) {
							nameProps.value = c.name;
						}
						if (sirenProps) {
							sirenProps.value = c.siren;
						}
						if (INSEEProps) {
							INSEEProps.value = `${c.siret} (${c.name} - ${c.address})`;
						}
						if (siretProps) {
							siretProps.value = c.siret;
						}
						if (cityOfRegistration) {
							cityOfRegistration.value = c.cityOfRegistration;
							if (companyCityProps) companyCityProps.value = c.cityOfRegistration;
						}
						if (postalCode) {
							postalCode.value = c.postalCode;
							if (companyPostalCodeProps) companyPostalCodeProps.value = c.postalCode;
						}
						if (businessLine) {
							businessLine.value = c.businessLine;
						}
						if (addressCompany) {
							addressCompany.value = c.address;
						}
						if (companyCountryProps) {
							companyCountryProps.value = 'FRA';
						}
						placesInputProps.value = `${c.address}, ${c.postalCode} ${c.cityOfRegistration}, France`;
						results = null;
					}
				}
			};
		};

		const query = removeWhiteSpace(INSEEProps?.value as string);

		if (!$isForeignCompany) {
			try {
				isFetching = true;
				let company = await getCompanyInfoBySIRET(query);
				if (company) {
					results = [createResultComponent(company)];
					INSEEProps.error = '';
				}
			} catch (e) {
				const error = e as HttpError;
				results = [];
				if (error?.body.message) {
					INSEEProps.error = error?.body?.message;
				} else {
					INSEEProps.error = $t('callError');
				}
				if (error.status === 404) {
					companyNotFoundProps.checked = true;
				}
				throw error;
			} finally {
				isFetching = false;
			}
		}
	});
</script>

<div class="space-y-4">
	<div class="relative">
		<InputText bind:props={INSEEProps} on:keyup={onKeyUp} on:paste={onKeyUp} />
		{#if results || isFetching}
			<ResultList {results} {isFetching} />
		{/if}
	</div>
	<InputCheckbox bind:props={companyNotFoundProps} />
	<div class="space-y-4 ml-3 {!companyNotFoundProps.checked ? 'hidden' : ''}">
		<InputText bind:props={nameProps} />
		<InputText bind:props={siretProps} />
		{#if !$isForeignCompany}
			<InputText bind:props={sirenProps} />
		{/if}
		<InputText bind:props={cityOfRegistration} />
		{#if postalCode}
			<InputText bind:props={postalCode} />
		{/if}
		{#if businessLine && !$isForeignCompany}
			<InputSelect bind:props={businessLine} />
		{/if}

		{#if addressCompany}
			<InputsAddresses
				additionalAddressOption={false}
				{placesInputProps}
				bind:addressProps={addressCompany}
				bind:cityProps={companyCityProps}
				bind:postalCodeProps={companyPostalCodeProps}
				bind:countryProps={companyCountryProps}
			/>
		{/if}
	</div>
</div>
